import type { DocumentTemplate } from "@vedaunion/shared";
import type { BlockDoc } from "../editor/types";
import { emptyDoc, newBlockId } from "../editor/utils";

/**
 * Pre-arranged document docs. Documents are *resource pages* — metadata +
 * a short introduction that frames the materials in the Resources rail.
 * These templates seed that introductory body. Authors pick a template at
 * create time; the block list is editable from there.
 */

export interface DocumentTemplatePreset {
  key: DocumentTemplate;
  label: string;
  blurb: string;
  doc: BlockDoc;
  previewHtml: string;
}

const block = (type: string, data: unknown) => ({ id: newBlockId(), type, data });

export const DOCUMENT_TEMPLATES: DocumentTemplatePreset[] = [
  {
    key: "mantra-page",
    label: "Mantra page",
    blurb: "A short, focused page for a single mantra — origin, meaning, how to recite. PDFs and audio go in the resources panel.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "Mantra title",
          align: "left",
          eyebrow: "Mantra",
        }),
        block("paragraph", {
          html: "<p>One paragraph orienting the reader: where this mantra comes from, who recites it, what it asks for.</p>",
          align: "left",
          variant: "lead",
        }),
        block("heading", { level: 2, text: "Meaning", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>A few lines on the meaning, in plain prose. Keep it close to lived practice.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "How to recite", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>The traditional context: count, posture, time of day, occasion. Note any pre-requisites and refer to the audio recording in the resources panel.</p>",
          align: "left",
          variant: "default",
        }),
        block("callout", {
          variant: "note",
          title: "Resources",
          html: "<p>The text in different scripts and the audio recordings are attached to this page — open the Resources panel.</p>",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">Mantra</p>
      <h1>Mantra title</h1>
      <p><em>One paragraph orienting the reader.</em></p>
      <h2>Meaning</h2>
      <h2>How to recite</h2>
    `,
  },

  {
    key: "sukta-page",
    label: "Sūkta page",
    blurb: "A Vedic hymn — the seer (ṛṣi), deity (devatā), metre (chandas), and the structure of the recitation.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "Sūkta title",
          align: "left",
          eyebrow: "Sūkta",
        }),
        block("paragraph", {
          html: "<p>An opening line that places this sūkta in its book — Ṛgveda, Atharvaveda, or another corpus — and gives a sense of its scale and use.</p>",
          align: "left",
          variant: "lead",
        }),
        block("heading", { level: 2, text: "The frame", align: "left", eyebrow: "" }),
        block("list", {
          ordered: false,
          items: [
            "<p><strong>Ṛṣi</strong> · the seer to whom the hymn is attributed</p>",
            "<p><strong>Devatā</strong> · the addressed deity</p>",
            "<p><strong>Chandas</strong> · the metre</p>",
            "<p><strong>Number of verses</strong> · …</p>",
          ],
        }),
        block("heading", { level: 2, text: "Themes", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Two or three sentences on what the sūkta concerns — the petitions, the imagery, the prevailing mood.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">Sūkta</p>
      <h1>Sūkta title</h1>
      <ul><li>Ṛṣi</li><li>Devatā</li><li>Chandas</li></ul>
      <h2>Themes</h2>
    `,
  },

  {
    key: "shloka-page",
    label: "Śloka page",
    blurb: "A single śloka or short cluster — verse + meaning + a brief commentary or context.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "Śloka title",
          align: "center",
          eyebrow: "Śloka",
        }),
        block("verse", {
          deva: "",
          iast: "",
          english: "",
          source: "",
        }),
        block("heading", { level: 2, text: "Context", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Where this verse sits in its source, who speaks it, and to whom.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "Reflection", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>A few lines that draw out a thread worth dwelling on.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow" style="text-align:center;">Śloka</p>
      <h1 style="text-align:center;">Śloka title</h1>
      <h2>Context</h2>
      <h2>Reflection</h2>
    `,
  },

  {
    key: "sadhana-guide",
    label: "Sādhana guide",
    blurb: "A structured practice — what it is, who it's for, the steps, the time it takes, and what to expect.",
    doc: {
      version: 1,
      blocks: [
        block("hero", {
          image: "",
          eyebrow: "Sādhana",
          title: "A practice in steps",
          subtitle: "An optional subtitle naming the lineage or aim.",
          align: "left",
          variant: "no-image",
          height: "short",
        }),
        block("paragraph", {
          html: "<p>One paragraph orienting the reader: what this sādhana is, where it comes from, and the heart of what it asks.</p>",
          align: "left",
          variant: "lead",
        }),
        block("heading", { level: 2, text: "Who this is for", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>Pre-requisites, level, and the kind of practitioner this suits.</p>",
          align: "left",
          variant: "default",
        }),
        block("heading", { level: 2, text: "What you'll need", align: "left", eyebrow: "" }),
        block("list", {
          ordered: false,
          items: [
            "<p>Quiet space and a fixed seat</p>",
            "<p>The text — see the resources panel for the script</p>",
            "<p>About N minutes a day, kept consistent for a fixed period</p>",
          ],
        }),
        block("heading", { level: 2, text: "The sequence", align: "left", eyebrow: "" }),
        block("list", {
          ordered: true,
          items: [
            "<p><strong>Preparation</strong> · how to begin each session.</p>",
            "<p><strong>Main practice</strong> · the heart of the sādhana.</p>",
            "<p><strong>Closing</strong> · how to end the session well.</p>",
          ],
        }),
        block("callout", {
          variant: "insight",
          title: "What to expect",
          html: "<p>A note on the rhythm of the practice — the early days, the first month, and the longer arc.</p>",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">Sādhana</p>
      <h1>A practice in steps</h1>
      <h2>Who this is for</h2>
      <h2>What you'll need</h2>
      <h2>The sequence</h2>
    `,
  },

  {
    key: "compilation",
    label: "Compilation",
    blurb: "A gathering of related materials — a curated collection page that ties together many shorter texts.",
    doc: {
      version: 1,
      blocks: [
        block("heading", {
          level: 1,
          text: "Compilation title",
          align: "left",
          eyebrow: "Compilation",
        }),
        block("paragraph", {
          html: "<p>A note on what's gathered here — the through-line that holds the pieces together.</p>",
          align: "left",
          variant: "lead",
        }),
        block("heading", { level: 2, text: "What's inside", align: "left", eyebrow: "" }),
        block("paragraph", {
          html: "<p>List the parts — see-also links to other documents and the resources panel for the materials themselves.</p>",
          align: "left",
          variant: "default",
        }),
      ],
    },
    previewHtml: `
      <p class="eyebrow">Compilation</p>
      <h1>Compilation title</h1>
      <h2>What's inside</h2>
    `,
  },
];

const BLANK_LABEL = "Blank";
const BLANK_BLURB = "An empty page. Add blocks from the palette as you go.";

export function findDocumentTemplate(key: DocumentTemplate): DocumentTemplatePreset {
  if (key === "blank") {
    return {
      key: "blank",
      label: BLANK_LABEL,
      blurb: BLANK_BLURB,
      doc: emptyDoc(),
      previewHtml: "",
    };
  }
  const t = DOCUMENT_TEMPLATES.find((tt) => tt.key === key);
  return t ?? DOCUMENT_TEMPLATES[0]!;
}
